// Navigation Component for Fortisys Website
// Based on Technical Specification Section 4.2

export class Navigation {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      scrollThreshold: 100,
      ...options
    };

    this.isScrolled = false;
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupDropdowns();
    this.handleInitialScroll();
  }

  bindEvents() {
    // Handle scroll for header state
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Handle dropdown interactions
    this.handleDropdownToggle = this.handleDropdownToggle.bind(this);
    this.handleDropdownClose = this.handleDropdownClose.bind(this);

    // Keyboard navigation
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.element.addEventListener('keydown', this.handleKeyDown);
  }

  setupDropdowns() {
    const dropdownTriggers = this.element.querySelectorAll('.header__nav-item');

    dropdownTriggers.forEach(trigger => {
      const dropdown = trigger.querySelector('.header__dropdown');
      if (!dropdown) return;

      // Mouse events
      trigger.addEventListener('mouseenter', () => {
        this.showDropdown(dropdown);
      });

      trigger.addEventListener('mouseleave', () => {
        this.hideDropdown(dropdown);
      });

      // Focus events for keyboard navigation
      const links = trigger.querySelectorAll('a');
      links.forEach((link, index) => {
        link.addEventListener('focus', () => {
          this.showDropdown(dropdown);
        });

        link.addEventListener('blur', (e) => {
          // Check if focus is moving to another element within the same dropdown
          const nextElement = e.relatedTarget;
          if (!trigger.contains(nextElement)) {
            setTimeout(() => {
              this.hideDropdown(dropdown);
            }, 100);
          }
        });
      });
    });
  }

  showDropdown(dropdown) {
    dropdown.style.opacity = '1';
    dropdown.style.visibility = 'visible';
    dropdown.style.transform = 'translateY(0)';
  }

  hideDropdown(dropdown) {
    dropdown.style.opacity = '0';
    dropdown.style.visibility = 'hidden';
    dropdown.style.transform = 'translateY(-10px)';
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const shouldBeScrolled = scrollTop > this.options.scrollThreshold;

    if (shouldBeScrolled !== this.isScrolled) {
      this.isScrolled = shouldBeScrolled;
      this.element.classList.toggle('header--scrolled', this.isScrolled);
    }
  }

  handleInitialScroll() {
    // Set initial state based on current scroll position
    this.handleScroll();
  }

  handleDropdownToggle(event) {
    event.preventDefault();
    const trigger = event.currentTarget.closest('.header__nav-item');
    const dropdown = trigger.querySelector('.header__dropdown');

    if (!dropdown) return;

    const isOpen = dropdown.style.visibility === 'visible';

    if (isOpen) {
      this.hideDropdown(dropdown);
    } else {
      // Close other dropdowns
      this.closeAllDropdowns();
      this.showDropdown(dropdown);
    }
  }

  handleDropdownClose(event) {
    // Close dropdown when clicking outside
    if (!event.target.closest('.header__nav-item')) {
      this.closeAllDropdowns();
    }
  }

  closeAllDropdowns() {
    const dropdowns = this.element.querySelectorAll('.header__dropdown');
    dropdowns.forEach(dropdown => {
      this.hideDropdown(dropdown);
    });
  }

  handleKeyDown(event) {
    // Handle escape key to close dropdowns
    if (event.key === 'Escape') {
      this.closeAllDropdowns();

      // Focus the first nav link
      const firstNavLink = this.element.querySelector('.header__nav-link');
      if (firstNavLink) {
        firstNavLink.focus();
      }
    }

    // Handle arrow key navigation within dropdowns
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const currentElement = document.activeElement;
      const dropdown = currentElement.closest('.header__dropdown');

      if (dropdown) {
        event.preventDefault();
        const links = dropdown.querySelectorAll('.header__dropdown-link');
        const currentIndex = Array.from(links).indexOf(currentElement);

        let nextIndex;
        if (event.key === 'ArrowDown') {
          nextIndex = currentIndex + 1 < links.length ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : links.length - 1;
        }

        links[nextIndex].focus();
      }
    }
  }

  // Public API methods
  setActiveNavItem(path) {
    // Remove active class from all nav items
    const navLinks = this.element.querySelectorAll('.header__nav-link');
    navLinks.forEach(link => {
      link.classList.remove('header__nav-link--active');
    });

    // Add active class to matching nav item
    const activeLink = this.element.querySelector(`[href="${path}"]`) ||
                       this.element.querySelector(`[href^="${path}"]`);

    if (activeLink) {
      activeLink.classList.add('header__nav-link--active');
    }
  }

  handleResize() {
    // Handle responsive behavior
    const isMobile = window.innerWidth < 992;

    if (isMobile) {
      this.closeAllDropdowns();
    }
  }

  destroy() {
    // Clean up event listeners
    window.removeEventListener('scroll', this.handleScroll);
    this.element.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleDropdownClose);

    // Reset header state
    this.element.classList.remove('header--scrolled');
  }
}
